/**
 * <facet-search> - type-to-narrow box for a facet value list.
 *
 * Purely visual: the server still renders the full list and nothing here
 * triggers a Shopify filter request. Works for both the desktop dropdown
 * (.facets__item) and the mobile submenu (.mobile-facets__item).
 */
if (!customElements.get('facet-search')) {
  // Keep checked values visible even if they don't match what is typed,
  // so people never lose sight of what is selected.
  const KEEP_SELECTED_VISIBLE = true;

  // Lower-case and strip diacritics (Spójka -> spojka), plus a few letters
  // that Unicode decomposition doesn't split.
  const normalize = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l')
      .replace(/ø/g, 'o')
      .replace(/æ/g, 'ae')
      .replace(/ß/g, 'ss');

  class FacetSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.facet-search__input');
      this.empty = this.querySelector('.facet-search__empty');
      this.details = this.closest('.js-filter');
      if (!this.input || !this.details) return;

      // Dawn's <facet-filters-form> listens for `input` on the whole form and
      // re-fetches results. Stop our events before they bubble that far.
      this.onInput = (event) => {
        event.stopPropagation();
        this.apply();
      };
      this.onChange = (event) => event.stopPropagation();

      this.onKeydown = (event) => {
        // Enter would otherwise submit the filter form
        if (event.key === 'Enter') event.preventDefault();
        // Note whether there was text before the browser's own Esc-to-clear runs
        if (event.key === 'Escape') this.hadTextOnEscape = this.input.value !== '';
      };

      this.onKeyup = (event) => {
        // First Esc only clears the text; a second Esc closes the dropdown (theme default)
        if (event.key !== 'Escape' || !this.hadTextOnEscape) return;
        this.hadTextOnEscape = false;
        event.stopPropagation();
        if (this.input.value) {
          this.input.value = '';
          this.apply();
        }
      };

      this.onToggle = () => {
        if (this.details.open) {
          // Focus the box on desktop only; on touch devices it would pop the keyboard
          if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
            this.input.focus({ preventScroll: true });
          }
        } else if (this.input.value) {
          this.input.value = '';
          this.apply();
        }
      };

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('change', this.onChange);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('keyup', this.onKeyup);
      this.details.addEventListener('toggle', this.onToggle);

      // After every checkbox click the theme swaps in a fresh <ul>, which would
      // wipe our hidden items. Re-apply the current query whenever the DOM changes.
      // (We only toggle classes, which this observer ignores, so there is no loop.)
      this.observer = new MutationObserver(() => this.apply());
      this.observer.observe(this.details, { childList: true, subtree: true });

      this.apply();
    }

    disconnectedCallback() {
      this.details?.removeEventListener('toggle', this.onToggle);
      this.observer?.disconnect();
    }

    apply() {
      const query = normalize(this.input.value.trim());
      let matches = 0;

      this.details.querySelectorAll('.facets__item, .mobile-facets__item').forEach((item) => {
        // The theme wraps the name in its own span, separate from the "(9)" count,
        // so typing a digit won't match the product counts.
        const label = item.querySelector('.facet-checkbox__text-label');
        const isMatch = !query || !label || normalize(label.textContent).includes(query);
        const keep = isMatch || (KEEP_SELECTED_VISIBLE && item.querySelector('input:checked'));

        item.classList.toggle('facet-search-hidden', !keep);
        if (isMatch) matches += 1;
      });

      if (this.empty) this.empty.hidden = !query || matches > 0;
    }
  }

  customElements.define('facet-search', FacetSearch);
}
